<template>
  <div>
    <label class="number">
      <span v-if="title" class="number__label">{{ title }}</span>
      <input
        class="number__input"
        type="number"
        v-bind:disabled="disabled"
        v-bind:class="appearanceClass"
        v-bind:max="max"
        v-bind:min="min"
        v-bind:name="name"
        v-bind:required="required"
        v-bind:step="step"
        v-on:blur="onBlur"
        v-on:focus="onFocus"
        v-on:input="onInput"
        v-mask="mask"
        v-model="inputValue"
      />
    </label>
    <div
      v-for="(error, index) in violations"
      v-bind:key="index"
      class="number__validate-error"
    >{{ error }}</div>
  </div>

</template>
<script>

/**
 * @description Название события фокуса.
 * @type {string}
 */
const NAME_EVENT_FOCUS = 'focus';

/**
 * @description Название события ввода.
 * @type {string}
 */
const NAME_EVENT_INPUT = 'input';

/**
 * @description Название события потери фокуса.
 * @type {string}
 */
const NAME_EVENT_BLUR = 'blur';

/**
 * @description Числовой ввод.
 * @type {Vue}
 */
export default {
  data() {
    return {
      /**
       * @description Класс оформления.
       * @type {string}
       */
      appearanceClass: this.withoutNativeControls ? 'without-controls' : '',
      /**
       * @description Значение поля.
       * @type {string}
       */
      inputValue: this.value ? this.value.toString() : '',
    };
  },
  methods: {
    /**
     * @description Форматировать значение.
     * @param event
     */
    formattedValue(event) {
      let { value } = event.target;
      if (value === '' && this.allowedNull) {
        this.inputValue = '';
        this.$emit(NAME_EVENT_INPUT, '');
        return;
      }
      value = value.replace(/[^0-9-]/g, '');
      if (value.indexOf('-') > 0) {
        value = value.replace('-', '');
      }
      if (this.min) {
        value = Math.max(Number(value), Number(this.min));
      }
      if (this.max) {
        value = Math.min(Number(value), Number(this.max));
      }
      this.inputValue = value;
    },
    /**
     * @description Обработать фокус.
     * @returns {void}
     */
    onFocus() {
      this.$emit(NAME_EVENT_FOCUS);
    },
    /**
     * @description Обработать ввод.
     * @param {InputEvent} event Событие.
     * @returns {void}
     */
    onInput(event) {
      this.formattedValue(event);
      this.$emit(NAME_EVENT_INPUT, this.inputValue);
    },
    /**
     * @description Обработать потерю фокуса.
     * @param event
     */
    onBlur(event) {
      this.formattedValue(event);
      this.$emit(NAME_EVENT_BLUR, this.inputValue);
    },
  },
  name: 'InputNumber',
  props: {
    /**
     * @description Максимальное значение.
     * @type {(String|Boolean)}
     */
    max: {
      default: () => false,
      type: [Boolean, String, Number],
    },
    /**
     * @description Минимальное значение.
     * @type {String}
     */
    min: { default: () => false, type: [String, Boolean] },
    /**
     * @description Уникальное имя элемента формы.
     * @type {String}
     */
    name: { default: () => false, type: [String, Boolean] },
    /**
     * @description Поле обязательно для заполнения.
     * @type {boolean|string}
     */
    required: { default: () => false, type: [String, Boolean] },
    /**
     * @description Шаг изменения значения.
     * @type {string}
     */
    step: { default: () => false, type: [String, Boolean] },
    /**
     * @description Заголовок.
     * @type {string}
     */
    title: { default: '', type: String },
    /**
     * @description Значение.
     * @type {number}
     */
    value: { required: true, type: [Number, String] },
    /**
     * @description Без встроенных элементов управления.
     */
    withoutNativeControls: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Деактивирован.
     */
    disabled: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Разрешена пустота.
     */
    allowedNull: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Нарушение ограничений.
     * @type {string}
     */
    violations: { default: () => [], type: Array },
    /**
     * @description Маска
     * @type String
     */
    mask: { default: () => '', type: String },
  },
  watch: {
    value(newValue) {
      if (this.allowedNull && (newValue === '' || newValue === null)) {
        this.inputValue = '';
      } else {
        this.inputValue = newValue.toString();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
@use "@/scss/fields";
.number {
  display: flex;
  flex-direction: column;
  &__label {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    margin: 0 0 2px 0;
    color: var(--unfocus-color);
  }
  &__input {
    @include fields.input-field();
  }
  &__input.without-controls {
    appearance: textfield;
  }
  &__input.without-controls::-webkit-inner-spin-button,
  &__input.without-controls::-webkit-outer-spin-button {
    appearance: none;
  }
  &__validate-error {
    color: var(--warning-color);
    font-size: .8rem;
  }
}
</style>
